import { ddbToFundDetail } from "@/data/fund-detail-mapper";
import {
  PREVIEW_READY_TYPE,
  isAllowedAdminOrigin,
  isPreviewDraftMessage,
  markerMatchesChanges,
} from "@/lib/preview-message";
import { Seo } from "@/lib/seo";
import type { Fund as DdbFund } from "@etfsite/db/types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FundDetailInner } from "./FundDetailPage";

/**
 * Chrome-less fund-detail PREVIEW surface, iframed by the admin dashboard.
 *
 * It renders the REAL fund-detail template — `FundDetailInner`, the exact
 * component the public `/:slug` page uses — from a DRAFT DDB Fund streamed in
 * over postMessage. Nothing is rebuilt or simulated; the draft is just swapped
 * in where the live API record would be.
 *
 * GATE BYPASS (by construction): the three gates on the public page —
 * `isFundLiveHere`, the white-label `window.location.replace` redirect, and the
 * `isPending` guard — all live in the FundDetailPage WRAPPER, not in
 * FundDetailInner. Reusing FundDetailInner directly therefore renders any draft
 * (even pre-launch / not-live / white-label) without those gates. We additionally
 * force `noindex` so the preview is never indexed.
 *
 * LIVE DATA: FundDetailInner's market-data hooks (useFundLiveData / useLiveNav /
 * useLiveHoldings) still run — QueryClientProvider is at the app root — so the
 * preview shows DRAFT CONFIG + REAL live market data, which is correct: a draft
 * only changes configurable fields, never market data. A brand-new ticker with
 * no ingested data simply shows "—", exactly as the live page would.
 *
 * STYLING: the `--etf-*` design tokens are scoped to `.etf-site` (not :root),
 * and `BufferRiskStrip` portals into `#etf-pre-nav-slot`. Both are provided by
 * `<Layout>` on the public route — since this route renders OUTSIDE Layout, the
 * wrapper below recreates them so the template styles + buffer risk strip work.
 */
export function PreviewPage() {
  const [draft, setDraft] = useState<DdbFund | null>(null);
  // Changed leaf paths + on/off, streamed alongside each draft from the admin.
  // Drives the change-highlight boxes applied after render (see below).
  const [changedPaths, setChangedPaths] = useState<string[]>([]);
  const [highlight, setHighlight] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      // Cross-origin channel — reject any sender that isn't a trusted admin.
      if (!isAllowedAdminOrigin(event.origin)) return;
      if (!isPreviewDraftMessage(event.data)) return;
      setDraft(event.data.draft);
      // Defensive reads — legacy admin builds may omit these fields.
      setChangedPaths(Array.isArray(event.data.changedPaths) ? event.data.changedPaths : []);
      setHighlight(event.data.highlight === true);
    }
    window.addEventListener("message", onMessage);

    // Announce readiness so the parent (admin) sends the first draft. Target the
    // referring admin origin when known + trusted; the ready ping carries no
    // data, so "*" is an acceptable fallback when the referrer is unavailable.
    if (window.parent && window.parent !== window) {
      let target = "*";
      try {
        const refOrigin = document.referrer ? new URL(document.referrer).origin : "";
        if (refOrigin && isAllowedAdminOrigin(refOrigin)) target = refOrigin;
      } catch {
        /* keep "*" */
      }
      window.parent.postMessage({ type: PREVIEW_READY_TYPE }, target);
    }

    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Apply the change-highlight boxes AFTER the draft renders. Runs in-app (same
  // origin) — we never reach into a cross-origin iframe; the admin sends the
  // path list and we resolve it to DOM nodes here. Each pass clears the prior
  // boxes, outlines every element whose `data-fund-field`/`data-fund-section`
  // matches a changed path (innermost match wins so a precise field box isn't
  // doubled by its containing section), then scrolls the first one into view.
  // useLayoutEffect so the clear/apply happens before paint (no flicker).
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the draft identity changes too (DOM is rebuilt).
  useLayoutEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const cleared = root.querySelectorAll<HTMLElement>("[data-fund-highlighted]");
    for (const el of cleared) clearHighlight(el);

    if (!highlight || changedPaths.length === 0) return;

    const markers = root.querySelectorAll<HTMLElement>("[data-fund-field],[data-fund-section]");
    const matched: HTMLElement[] = [];
    for (const el of markers) {
      const field = el.getAttribute("data-fund-field");
      const section = el.getAttribute("data-fund-section");
      if (
        (field && markerMatchesChanges(field, changedPaths)) ||
        (section && markerMatchesChanges(section, changedPaths))
      ) {
        matched.push(el);
      }
    }
    // Keep only the innermost matches: drop any element that contains another
    // match (prefer the precise field box over its surrounding section box).
    const innermost = matched.filter(
      (el) => !matched.some((other) => other !== el && el.contains(other)),
    );
    for (const el of innermost) applyHighlight(el);
    innermost[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [draft, changedPaths, highlight]);

  return (
    // `.etf-site` scopes the design tokens; the slot div is BufferRiskStrip's
    // portal target. Mirrors <Layout>'s wrapper minus the nav/footer chrome.
    <div className="etf-site min-h-screen flex flex-col bg-[var(--etf-black)] text-[var(--etf-white)]">
      <div id="etf-pre-nav-slot" />
      {draft ? (
        <div ref={contentRef}>
          <Seo
            title={`Preview — ${draft.ticker ?? "fund"}`}
            description="Draft fund preview."
            noindex
          />
          <FundDetailInner fund={ddbToFundDetail(draft)} />
        </div>
      ) : (
        <>
          <Seo title="Fund preview" description="Draft fund preview." noindex />
          <div className="flex flex-1 items-center justify-center px-6 text-center">
            <p className="font-[family-name:var(--font-geist)] text-sm text-[var(--etf-white3)]">
              Waiting for the admin preview…
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// ── Change-highlight box styling (applied imperatively post-render) ───────────
//
// Inline styles (not a Tailwind class) because the boxed nodes are arbitrary
// deep descendants we resolve at runtime; a marker attribute (`data-fund-
// highlighted`) tracks what we styled so the next pass can cleanly revert it
// without stomping any author styles. A 2px accent ring + subtle bg matches the
// admin's `--etf-orange` accent.
function applyHighlight(el: HTMLElement): void {
  el.dataset.fundHighlighted = "true";
  el.style.outline = "2px solid var(--etf-orange)";
  el.style.outlineOffset = "2px";
  el.style.borderRadius = "2px";
  el.style.backgroundColor = "rgba(255, 85, 0, 0.08)";
  el.style.transition = "outline-color 0.2s ease, background-color 0.2s ease";
}

function clearHighlight(el: HTMLElement): void {
  delete el.dataset.fundHighlighted;
  el.style.outline = "";
  el.style.outlineOffset = "";
  el.style.borderRadius = "";
  el.style.backgroundColor = "";
  el.style.transition = "";
}
